import React, { useContext, useEffect } from "react";
import '@progress/kendo-theme-default/dist/all.css';
import { Route, Routes, Navigate } from 'react-router-dom';
import RequireAuth from './shared/RequireAuth';
import PublicLayout from './shared/layouts/PublicLayout';
import Login from './components/login/login';
import RecoverPassword from './components/recoverPassword/recoverPassword';
import ChangePassword from './components/changePassword/changePassword';

import { observer } from "mobx-react";

import Timesheet from './components/timesheet/timesheet';
import TimesheetList from './components/timesheet/timesheet-list';
import UsersAdmin from './components/users/users.admin';
import UsersEdit from './components/users/users.edit';
import ActivitiesAdmin from './components/activities/activities.admin';
import ActivitiesEdit from './components/activities/activities.edit';
import ProjectsAdmin from './components/projects/projects.admin';
import ClientsAdmin from './components/clients/clients.admin';
import ClientsEdit from './components/clients/clients.edit';
import CompaniesAdmin from './components/companies/companies.admin';
import CompaniesEdit from './components/companies/companies.edit';

import DocumentsAdmin from './components/documents/documents.admin';
import UserDocumentsAdmin from './components/documents/userDocuments.admin';
import ReportsAdmin from './components/reports/reports.admin';
import TemplatesAdmin from './components/templates/templates.admin';
import TemplatesEdit from './components/templates/templates.edit';
import CurrenciesAdmin from './components/currencies/currencies.admin';
import CurrenciesEdit from './components/currencies/currencies.edit';
import ProjectEdit from './components/projects/project.edit';
import TwoFactorAuthentication from './components/twoFactorAuthentication/twoFactorAuthentication';
import VatAdmin from './components/vatSettings/vat.admin';
import VatEdit from './components/vatSettings/vat.edit';
import RatesAdmin from './components/rates/rates.admin';
import RatesEdit from './components/rates/rates.edit';
import PackageExpensesAdmin from './components/packageExpenses/packageExpenses.admin';
import PackageExpensesEdit from './components/packageExpenses/packageExpenses.edit';
import CountryCurrenciesAdmin from './components/countryCurrencies/countryCurrencies.admin';
import CountryCurrenciesEdit from './components/countryCurrencies/countryCurrencies.edit';
import ExpensesWithCostsAdmin from './components/expensesWithCosts/expensesWithCosts.admin';
import ExpensesWithCostsEdit from './components/expensesWithCosts/expensesWithCosts.edit';

import InternalExpensesSummary from './components/expenses/internal/internalExpensesSummary';
import ExpensesWithPackageForm from './components/expenses/internal/withPackages/expensesWithPackage.form';

import WinbizSettings from './components/winbiz/winbiz.settings';

import './index.scss';
import '../node_modules/bootstrap/dist/js/bootstrap.bundle';
import { useStores } from "./core/stores/rootStore";
import CacheBuster from 'react-cache-buster';
import packageInfo from '../package.json';
import EmailSettings from "./components/email/email.settings";
import ExpensevatAdmin from "./components/expenseVatSettings/expensevat.admin";
import ExpensevatEdit from "./components/expenseVatSettings/expensevat.edit";
import ExpensesWithCostsForm from "./components/expenses/internal/withCosts/expensesWithCosts.form";

import BilledExpensesForm from './components/expenses/billed/billedExpenses.form';


const App = () => {
    const { loginStore } = useStores();
    const { accountInfo, isLoggedIn } = loginStore;
    const isProduction = process.env.NODE_ENV === 'production';

    return (
        <CacheBuster
            currentVersion={packageInfo.version}
            isEnabled={false} //If false, the library is disabled.
            isVerboseMode={false} //If true, the library writes verbose logs to console.
            loadingComponent={<span>Clearing cache..</span>} //If not pass, nothing appears at the time of new version check.            
            onCacheClear={() => window.location.reload()}
        >
            <Routes>
                <Route path="*" element={<Navigate to="/" replace />} />
                <Route path="/" element={
                    isLoggedIn() ?
                        <Navigate to="/timesheets" replace /> :
                        <Navigate to="/login" replace />
                } />
                <Route path="login" element={
                    <PublicLayout>
                        <Login />
                    </PublicLayout>}
                />
                <Route path="recover-password" element={
                    <PublicLayout>
                        <RecoverPassword />
                    </PublicLayout>}
                />

                <Route path="change-password" element={
                    <PublicLayout>
                        <ChangePassword isChangePasswordPage={true} />
                    </PublicLayout>}
                />

                <Route path="two-factor-authentication/:token" element={
                    <PublicLayout>
                        <TwoFactorAuthentication />
                    </PublicLayout>}
                />
                <Route path="timesheets/:id" element={
                    <RequireAuth>
                        <Timesheet />
                    </RequireAuth>}
                />
                <Route path="timesheets" element={
                    <RequireAuth>
                        <TimesheetList />
                    </RequireAuth>}
                />
                <Route path="profile" element={
                    <RequireAuth activeFor={[3]}>
                        <UsersEdit />
                    </RequireAuth>}
                />
                
                

                <Route path="administration">

                    <Route path="email" element={
                        <RequireAuth activeFor={[1]}>
                            <EmailSettings />
                        </RequireAuth>}
                    />

                    <Route path="winbiz" element={
                        <RequireAuth activeFor={[1]}>
                            <WinbizSettings />
                        </RequireAuth>}
                    />

                    <Route path="users" element={
                        <RequireAuth activeFor={[1]}>
                            <UsersAdmin />
                        </RequireAuth>}
                    />

                    <Route path="users/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <UsersEdit />
                        </RequireAuth>}
                    />

                    <Route path="activities" element={
                        <RequireAuth activeFor={[1]}>
                            <ActivitiesAdmin />
                        </RequireAuth>}
                    />

                    <Route path="activities/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <ActivitiesEdit />
                        </RequireAuth>}
                    />

                    <Route path="templates" element={
                        <RequireAuth activeFor={[1]}>
                            <TemplatesAdmin />
                        </RequireAuth>}
                    />

                    <Route path="templates/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <TemplatesEdit />
                        </RequireAuth>}
                    />

                    <Route path="currencies" element={
                        <RequireAuth activeFor={[1]}>
                            <CurrenciesAdmin />
                        </RequireAuth>}
                    />

                    <Route path="currencies/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <CurrenciesEdit />
                        </RequireAuth>}
                    />

                    <Route path="projects" element={
                        <RequireAuth activeFor={[1]}>
                            <ProjectsAdmin />
                        </RequireAuth>}
                    />

                    <Route path="projects/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <ProjectEdit />
                        </RequireAuth>}
                    />

                    <Route path="clients" element={
                        <RequireAuth activeFor={[1]}>
                            <ClientsAdmin />
                        </RequireAuth>}
                    />
                    <Route path="clients/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <ClientsEdit />
                        </RequireAuth>}
                    />

                    <Route path="companies" element={
                        <RequireAuth activeFor={[1]}>
                            <CompaniesAdmin />
                        </RequireAuth>}
                    />
                    <Route path="companies/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <CompaniesEdit />
                        </RequireAuth>}
                    />

                    <Route path="vat" element={
                        <RequireAuth activeFor={[1]}>
                            <VatAdmin />
                        </RequireAuth>}
                    />
                    <Route path="vat/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <VatEdit />
                        </RequireAuth>}
                    />

                    <Route path="rates" element={
                        <RequireAuth activeFor={[1]}>
                            <RatesAdmin />
                        </RequireAuth>}
                    />
                    <Route path="rates/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <RatesEdit />
                        </RequireAuth>}
                    />
                    <Route path="package-expenses" element={
                        <RequireAuth activeFor={[1]}>
                            <PackageExpensesAdmin />
                        </RequireAuth>}
                    />
                    <Route path="package-expenses/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <PackageExpensesEdit />
                        </RequireAuth>}
                    />

                    <Route path="country-currencies" element={
                        <RequireAuth activeFor={[1]}>
                            <CountryCurrenciesAdmin />
                        </RequireAuth>}
                    />
                    <Route path="country-currencies/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <CountryCurrenciesEdit />
                        </RequireAuth>}
                    />

                    <Route path="expenses-with-costs" element={
                        <RequireAuth activeFor={[1]}>
                            <ExpensesWithCostsAdmin />
                        </RequireAuth>}
                    />
                    <Route path="expenses-with-costs/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <ExpensesWithCostsEdit />
                        </RequireAuth>}
                    />

                    <Route path="expensevats" element={
                        <RequireAuth activeFor={[1]}>
                            <ExpensevatAdmin />
                        </RequireAuth>}
                    />
                    <Route path="expensevats/:id" element={
                        <RequireAuth activeFor={[1]}>
                            <ExpensevatEdit />
                        </RequireAuth>}
                    />


                </Route>


                <Route path="expenses">
                    <Route path="internal" element={<RequireAuth activeFor={[3]}>
                        <InternalExpensesSummary />
                    </RequireAuth>} />

                    <Route path="with-package/:year/:month/:devId" element={<RequireAuth activeFor={[1, 3]}>
                        <ExpensesWithPackageForm />
                    </RequireAuth>} />

                    <Route path="with-package/:year/:month/" element={<RequireAuth activeFor={[1, 3]}>
                        <ExpensesWithPackageForm />
                    </RequireAuth>} />

                    <Route path="with-costs/:year/:month/:currencyId/" element={<RequireAuth activeFor={[1, 3]}>
                        <ExpensesWithCostsForm />
                    </RequireAuth>} />

                    <Route path="with-costs/:year/:month/:currencyId/:devId" element={<RequireAuth activeFor={[1, 3]}>
                        <ExpensesWithCostsForm />
                    </RequireAuth>} />

                    <Route path="billed/:year/:month/:activityId"
                        element={
                            <RequireAuth activeFor={[1 ,2 ,3]}>
                                <BilledExpensesForm />
                            </RequireAuth>
                        }
                    />
                </Route>

                <Route path="documents" element={
                    <RequireAuth activeFor={[1, 3]}>
                        <UserDocumentsAdmin />
                    </RequireAuth>}
                />

                <Route path="reports" element={

                    <RequireAuth activeFor={[1]}>
                        <ReportsAdmin />
                    </RequireAuth>
                }
                />
            </Routes>
        </CacheBuster>
    );
};

export default observer(App); 